import React, { useCallback, useEffect, useState } from "react"
import ContentLoader from "react-content-loader"
import Input from "../../Input"
import {
  clearStudySynopsis,
  loadStudySynopsis,
  updateStudySynopsis,
} from "../../../store/entity/entity.actions"
import { useDispatch } from "react-redux"
import Button from "../../Button"
import Modal from "../../Modal"
import Dropzone from "../../DropZone"
import { bytesToMB } from "../../../helpers"
import { addAlertWithTimeout, AlertType } from "../../../utils/alertUtils"
const StudySynopsis = ({ studyId }) => {
  const dispatch = useDispatch()
  const [editSynopsis, setEditSynopsis] = useState(false)
  const [synopsis, setSynopsis] = useState(null)
  const [newSynopsisPath, setNewSynopsisPath] = useState("")
  const [synopsisLoading, setSynopsisLoading] = useState(false)
  const [selectSynopsisUrl, setSelectSynopsisUrl] = useState(false)
  const [synopsisSaving, setSynopsisSaving] = useState(false)
  const [synopsisFileModal, setSynopsisFileModal] = useState(false)
  const [synopsisFile, setSynopsisFile] = useState(null)
  const [synopsisEdited, setSynopsisEdited] = useState(false)
  const [confirmClearSynopsisModal, setConfirmClearSynopsisModal] =
    useState(false)

  const loadSynopsis = useCallback(() => {
    setSynopsisLoading(true)

    dispatch(loadStudySynopsis({ id: studyId }))
      .unwrap()
      .then(r => {
        if (r !== undefined) {
          if (!r.data?.is_file) {
            setNewSynopsisPath(r.data?.path ?? "")
          }
          setSynopsis(r.data)
        }

        setSynopsisLoading(false)
      })
  }, [dispatch, studyId])

  const saveSynopsis = async () => {
    let formData = new FormData()

    setSynopsisSaving(true)

    if (synopsisFile) {
      formData.append("file", synopsisFile)
      formData.append("is_file", "true")
    } else {
      formData.append("path", newSynopsisPath)
      formData.append("is_file", "false")
    }

    dispatch(updateStudySynopsis({ id: studyId, data: formData }))
      .unwrap()
      .then(r => {
        if (r !== undefined) {
          setSynopsis(r.data)

          addAlertWithTimeout(
            "Study successfully updated",
            <>
              <p>Study Synopsis has been updated successfully.</p>
            </>,
            AlertType.Success
          )
        }

        setSynopsisSaving(false)
        setEditSynopsis(false)
      })
  }

  const clearSynopsis = () => {
    setSynopsisSaving(true)

    dispatch(clearStudySynopsis({ id: studyId }))
      .unwrap()
      .then(r => {
        if (r !== undefined) {
          addAlertWithTimeout(
            "Study successfully updated",
            <>
              <p>Study Synopsis has been removed successfully.</p>
            </>,
            AlertType.Warning
          )

          setSynopsis(null)
          setConfirmClearSynopsisModal(false)
        }

        setSynopsisSaving(false)
        setEditSynopsis(false)
      })
  }

  useEffect(() => {
    loadSynopsis()
  }, [loadSynopsis])

  return (
    <>
      <div className="bg-white rounded-lg px-5 border mt-2.5 p-5">
        <div className="flex flex-row justify-between items-center">
          <h4 className="font-bold text-xl">Study Synopsis</h4>
          <div>
            {editSynopsis && (
              <Button
                className={`px-8 py-1.5 mb-0 mr-2 text-green-700 ${
                  !synopsisEdited || synopsisSaving ? "opacity-40" : ""
                }`}
                onClick={saveSynopsis}
                disabled={!synopsisEdited || synopsisSaving}
              >
                {synopsisSaving ? "Saving..." : "Save"}
              </Button>
            )}
            <Button
              className="px-3 py-1.5 mb-0"
              onClick={() => {
                setSelectSynopsisUrl(false)
                setSynopsisFile(null)
                setEditSynopsis(prev => !prev)
              }}
            >
              {editSynopsis ? (
                "Cancel"
              ) : (
                <svg
                  width="19"
                  height="19"
                  viewBox="0 0 19 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17.71 5.04C18.1 4.65 18.1 4 17.71 3.63L15.37 1.29C15 0.899998 14.35 0.899998 13.96 1.29L12.12 3.12L15.87 6.87M0 15.25V19H3.75L14.81 7.93L11.06 4.18L0 15.25Z"
                    fill="black"
                  />
                </svg>
              )}
            </Button>
          </div>
        </div>
        <hr />
        <div className="mt-5 py-3 px-5 rounded-lg border flex flex-row items-center my-2">
          <span className="w-1/2 inline-block font-medium">Study synopsis</span>
          <div className="w-1/2">
            {synopsisLoading ? (
              <ContentLoader
                speed={2}
                width="100%"
                height={24}
                backgroundColor="#c2c2c2"
                foregroundColor="#ecebeb"
              >
                <rect x="0" y="2" rx="8" ry="8" width="90%" height="12" />
              </ContentLoader>
            ) : editSynopsis ? (
              selectSynopsisUrl ? (
                <div className="flex items-center">
                  <Input
                    value={newSynopsisPath}
                    onChange={e => {
                      setNewSynopsisPath(e.target.value)
                      setSynopsisEdited(true)
                    }}
                    placeholder="Enter URL..."
                  />
                  <button
                    className="pl-2.5 hover:opacity-70 transition h-full text-red-500"
                    onClick={() => {
                      setSynopsisEdited(false)
                      setSelectSynopsisUrl(false)
                      setNewSynopsisPath("")
                    }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="w-fit mx-auto border border-dashed rounded-xl flex flex-row text-dark-gray font-light">
                  {synopsisFile ? (
                    <>
                      <button
                        className="pl-4 pr-2.5 py-4 hover:opacity-70 transition h-full flex flex-row justify-around"
                        onClick={() => setSynopsisFileModal(true)}
                      >
                        <span className="max-w-[200px] truncate mr-1.5">
                          {synopsisFile.name}
                        </span>
                        <span className="font-medium">
                          {bytesToMB(synopsisFile.size)} MB
                        </span>
                      </button>
                      <button
                        className="pr-4 pl-2.5 py-4 hover:opacity-70 transition h-full text-red-500"
                        onClick={() => {
                          setSynopsisFile(null)
                          setSynopsisEdited(false)
                        }}
                      >
                        Clear
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="pl-4 pr-2.5 py-4 hover:opacity-70 transition h-full"
                        onClick={() => setSynopsisFileModal(true)}
                      >
                        {synopsis && synopsis.is_file
                          ? "Upload new file"
                          : "Select file"}
                      </button>
                      <button
                        className="px-2.5 py-4 hover:opacity-70 transition h-full"
                        onClick={() => setSelectSynopsisUrl(true)}
                      >
                        {synopsis && !synopsis.is_file
                          ? "Edit URL"
                          : "Select URL"}
                      </button>
                      {synopsis && (
                        <button
                          className="pr-4 pl-2.5 py-4 hover:opacity-70 transition h-full text-red-500"
                          onClick={() => setConfirmClearSynopsisModal(true)}
                        >
                          Clear all
                        </button>
                      )}
                    </>
                  )}
                </div>
              )
            ) : synopsis ? (
              synopsis.is_file ? (
                <a
                  href={synopsis.link}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full block border border-dashed border-light-gray text-light-gray rounded-lg font-light px-3 truncate hover:opacity-80 transition"
                >
                  {synopsis.path}
                </a>
              ) : (
                <a
                  href={synopsis.link}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full block text-blue hover:opacity-70 transition inline-block truncate"
                >
                  {synopsis.path}
                </a>
              )
            ) : (
              <div className="text-light-gray text-center">
                No file uploaded
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        title="Upload file"
        size="lg"
        isShown={synopsisFileModal}
        setIsShown={setSynopsisFileModal}
        submitActionActive={synopsisFile}
        noButtons={true}
      >
        <Dropzone
          setFile={setSynopsisFile}
          onDropAction={() => {
            setSynopsisFileModal(false)
            setSynopsisEdited(true)
          }}
        />
      </Modal>

      <Modal
        title="Clear Synopsis"
        size="md"
        isShown={confirmClearSynopsisModal}
        setIsShown={setConfirmClearSynopsisModal}
        onSubmit={clearSynopsis}
        submitText="Yes"
      >
        <p>Are you sure you want to clear Study Synopsis?</p>
      </Modal>
    </>
  )
}

export default StudySynopsis
